// lib/config.ts
import { env } from "@/env.mjs";

/**
 * API Configuration utility
 * Provides centralized configuration for API endpoints with environment-based switching
 */
export class ApiConfig {
  /**
   * Get the base API server URL based on environment configuration
   * Falls back to production URL if not specified
   */
  static getApiServerUrl(): string {
    // Use environment variable if set, otherwise fallback to production
    return env.NEXT_PUBLIC_API_SERVER_URL || "https://api.fexr.io";
  }

  /**
   * Get the full API endpoint URL by combining base URL with endpoint path
   * @param endpoint - The API endpoint path (e.g., '/v1/vaults')
   * @returns Complete URL for the API endpoint
   */
  static getApiEndpoint(endpoint: string): string {
    const baseUrl = this.getApiServerUrl();
    // Ensure endpoint starts with '/'
    const cleanEndpoint = endpoint.startsWith("/") ? endpoint : `/${endpoint}`;
    return `${baseUrl}${cleanEndpoint}`;
  }

  /**
   * Check if we're currently in development mode
   */
  static isDevelopment(): boolean {
    return process.env.NODE_ENV === "development";
  }

  /**
   * Check if we're using localhost API
   */
  static isLocalhost(): boolean {
    return this.getApiServerUrl().includes("localhost");
  }

  /**
   * Get environment-specific configuration
   */
  static getEnvironmentInfo() {
    return {
      apiUrl: this.getApiServerUrl(),
      isDevelopment: this.isDevelopment(),
      isLocalhost: this.isLocalhost(),
      environment: process.env.NODE_ENV || "development",
    };
  }
}

/**
 * Legacy support - export the API server URL directly
 * @deprecated Use ApiConfig.getApiServerUrl() instead
 */
export const API_SERVER_URL = ApiConfig.getApiServerUrl();
